import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  HostedSummariesResponse,
  HostedSnapshotDomainsResponse,
  HostedSnapshotListResponse,
  HostedSnapshotRowsResponse,
  DiffResult,
  MonthlyDiffResponse,
  BillingOverviewResponse,
  DomainBillingConfig,
  DomainOverviewRow,
} from '../models/billing.model';
import { NetsapiensDomain, Product } from '@voice/utils';

export interface TrunkingDomainsResponse {
  domains: Partial<NetsapiensDomain>[];
  total: number;
  includeAdminAndDemo: boolean;
}

export interface TrunkingSummaryRow {
  CUSTOMER: string | null;
  DOMAIN: string;
  TRUNKS_CHANNELS: number;
  'TOTAL 911': number;
  'TOTAL TNs': number;
  'BILLABLE 911': number;
  'BILLABLE TNS': number;
  'PHYSICAL FAX': number;
  'VIRTUAL FAX': number;
}

export interface RevioCustomer {
  customerId: number;
  parentId?: number;
  customerName: string;
  class: string;
  finance: object;
  isParent?: boolean;
  masId?: string;
}

export interface RevioServiceProduct {
  serviceProductId: number;
  customerId: number; // site id
  serviceId: number;
  productId: number;
  status: 'ACTIVE' | 'CANCELED';
  description: string;
  code: string; // product code
  rate: number;
  quantity: number;
  customerName: string; // site (display) name
  activatedDate?: string;
}

export interface NonBillableEntryDto {
  id: number;
  domain: string;
  site: string | null;
  serviceCode: string;
  userId: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  reason: string | null;
  createdAt: string;
}

export interface CreateNonBillableEntryRequest {
  domain: string;
  site?: string | null;
  serviceCode: string;
  userId?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  reason?: string | null;
}

export interface ImportNonBillableEntryRequest {
  domain: string;
  serviceCode: string;
  site?: string | null;
  userId?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  reason?: string | null;
}

export interface ImportNonBillableResult {
  inserted: number;
  skipped: number;
  errors: Array<{ row: number; message: string }>;
}

export type DeviceRoleCategory =
  | 'ANALOG'
  | 'PAGING'
  | 'LIFE_SAFETY'
  | 'LOUD_RINGER'
  | 'SIP_INTEGRATION'
  | 'HANDSET';

/** EXACT_MODEL_NAME: modelName matched against NS device-models-model. USER_AGENT_CONTAINS:
 * modelName is a substring matched against the device's registration User-Agent — for
 * customer-owned/software devices that never report a model name (e.g. FreeSWITCH, Interlock). */
export type DeviceModelMatchType = 'EXACT_MODEL_NAME' | 'USER_AGENT_CONTAINS';

export interface DeviceModelDto {
  id: number;
  modelName: string;
  category: DeviceRoleCategory;
  matchType: DeviceModelMatchType;
  isActive: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDeviceModelRequest {
  modelName: string;
  category: DeviceRoleCategory;
  matchType?: DeviceModelMatchType;
  isActive?: boolean;
  notes?: string | null;
}

export interface UpdateDeviceModelRequest {
  modelName?: string;
  category?: DeviceRoleCategory;
  matchType?: DeviceModelMatchType;
  isActive?: boolean;
  notes?: string | null;
}

export interface NsCatalogEntry {
  modelName: string;
  brand: string;
}

export type DiscoveredModelSource = 'inventory' | 'user-agent';

export interface DiscoveredModel {
  /** For inventory rows the NS model name; for user-agent rows the suggested match pattern. */
  modelName: string;
  brand: string;
  domainCount: number;
  domains: string[];
  /** 'user-agent' rows can only be matched by a USER_AGENT_CONTAINS entry. */
  source: DiscoveredModelSource;
  /** For user-agent rows: a full UA string seen, as evidence for assigning a category. */
  sampleUserAgent?: string;
  /** For user-agent rows: how many registered devices reported a matching UA. */
  deviceCount?: number;
}

export interface BulkCreateDeviceModelRequest {
  models: CreateDeviceModelRequest[];
}

export interface DomainItem {
  domain: string;
}

/** Where a scan's user-agent matches came from, so the UI can show how fresh they are. */
export interface ScanDomainSource {
  origin: 'evaluation' | 'live-scan' | 'unavailable';
  collectedAt?: string;
  /** Why a live sweep was needed, when origin is 'live-scan'. */
  reason?: 'never-evaluated' | 'catalog-newer-than-snapshot';
}

export interface ScanDomainModelHit {
  count: number;
  extensions: string[];
  /** For user-agent matches: the distinct user-agents that produced them. */
  userAgents?: string[];
}

export interface ScanDomainResult {
  domain: string;
  models: Record<string, ScanDomainModelHit>;
  userAgentSource?: ScanDomainSource;
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  private readonly base = `${environment.voiceAPIConfig.uri}/billing`;

  private readonly snapshotsBase = `${this.base}/hosted-snapshots`;

  constructor(private http: HttpClient) {}

  public getHostedSummaries(
    domains: string[]
  ): Observable<HostedSummariesResponse> {
    const url = `${this.base}/hosted-summary`;
    return this.http.post<HostedSummariesResponse>(url, { domains });
  }

  public getHostedSummariesAsCSV(domains: string[]): Observable<string> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers, responseType: 'text' as 'json' };
    const url = `${this.base}/hosted-summary.csv`;
    return this.http.post<string>(url, { domains }, options);
  }

  public getTrunkingDomains(
    includeAdminAndDemo = false
  ): Observable<TrunkingDomainsResponse> {
    const url = `${this.base}/trunking-domains?includeAdminAndDemo=${includeAdminAndDemo}`;
    return this.http.get<TrunkingDomainsResponse>(url);
  }

  public getTrunkingSummary(
    domains: string[]
  ): Observable<TrunkingSummaryRow[]> {
    const url = `${this.base}/trunking-summary`;
    return this.http.post<TrunkingSummaryRow[]>(url, { domains });
  }

  public getTrunkingSummaryCsv(domains: string[]): Observable<Blob> {
    const url = `${this.base}/trunking-summary.csv`;
    return this.http.post(url, { domains }, { responseType: 'blob' });
  }

  public getHostedSnapshotDomains(): Observable<HostedSnapshotDomainsResponse> {
    const url = `${this.snapshotsBase}/domains`;
    return this.http.get<HostedSnapshotDomainsResponse>(url);
  }

  public getHostedSnapshots(
    domain: string,
    limit = 30
  ): Observable<HostedSnapshotListResponse> {
    const url = `${this.snapshotsBase}?domain=${encodeURIComponent(
      domain
    )}&limit=${limit}`;
    return this.http.get<HostedSnapshotListResponse>(url);
  }

  public getHostedSnapshotRows(
    snapshotId: string
  ): Observable<HostedSnapshotRowsResponse> {
    const url = `${this.snapshotsBase}/rows?snapshotId=${encodeURIComponent(
      snapshotId
    )}`;
    return this.http.get<HostedSnapshotRowsResponse>(url);
  }

  public getHostedSnapshotsDiff(
    fromId: string,
    toId: string
  ): Observable<DiffResult> {
    const url = `${this.snapshotsBase}/diff?from=${encodeURIComponent(
      fromId
    )}&to=${encodeURIComponent(toId)}`;
    return this.http.get<DiffResult>(url);
  }

  public getHostedSnapshotsDiffCsv(
    fromId: string,
    toId: string
  ): Observable<Blob> {
    const url = `${this.snapshotsBase}/diff.csv?from=${encodeURIComponent(
      fromId
    )}&to=${encodeURIComponent(toId)}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  public getHostedMonthlyDiff(
    domain: string,
    year: number,
    month: number
  ): Observable<MonthlyDiffResponse> {
    const url = `${this.snapshotsBase}/monthly-diff?domain=${encodeURIComponent(
      domain
    )}&year=${year}&month=${month}`;
    return this.http.get<MonthlyDiffResponse>(url);
  }

  public getHostedMonthlyDiffCsv(
    domain: string,
    year: number,
    month: number
  ): Observable<Blob> {
    const url = `${
      this.snapshotsBase
    }/monthly-diff.csv?domain=${encodeURIComponent(
      domain
    )}&year=${year}&month=${month}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  public getRevioCustomers(): Observable<RevioCustomer[]> {
    const url = `${this.base}/customers`;
    return this.http.get<RevioCustomer[]>(url);
  }

  public getRevioStatistics(): Observable<{
    customers: number;
    sites: number;
  }> {
    const url = `${this.base}/customers/statistics`;
    return this.http.get<{ customers: number; sites: number }>(url);
  }
  public revioSearchCustomersByName(name: string) {
    const url = `${this.base}/customers/search`;
    const params = new HttpParams().set('name', name);
    return this.http.get<RevioCustomer[]>(url, { params });
  }

  public revioSearchCustomersByDomain(domain: string) {
    const url = `${this.base}/customers/search-by-domain`;
    const params = new HttpParams().set('domain', domain);
    return this.http.get<RevioCustomer[]>(url, { params });
  }

  public revioGetCustomerSites(parentId: number) {
    const url = `${this.base}/customers/${parentId}/sites`;
    return this.http.get<RevioCustomer[]>(url);
  }

  public getRevioCustomerById(id: number): Observable<RevioCustomer[]> {
    const url = `${this.base}/customers/${id}`;
    return this.http.get<RevioCustomer[]>(url);
  }

  public getBillingActivation(customerId: number): Observable<{
    enabled: boolean;
    activationDate: string | null;
  } | null> {
    const url = `${this.base}/customers/${customerId}/billing-activation`;
    return this.http.get<{
      enabled: boolean;
      activationDate: string | null;
    } | null>(url);
  }

  public updateCustomerMasId(
    customerId: number,
    masId: string
  ): Observable<{ success: boolean }> {
    const url = `${this.base}/customers/${customerId}/mas-id`;
    return this.http.patch<{ success: boolean }>(url, { mas_id: masId });
  }

  public revioGetServiceProducts(parentId: number) {
    const url = `${this.base}/customers/${parentId}/service-products`;
    return this.http.get<RevioServiceProduct[]>(url);
  }

  public revioUpdateQuantity(
    parentId: number,
    serviceProductId: number,
    quantity: number,
    effectiveDate?: Date
  ) {
    const url = `${this.base}/customers/${parentId}/service-products/${serviceProductId}/quantity`;
    const body: { quantity: number; effective_date?: string } = {
      quantity,
    };
    if (effectiveDate) {
      body.effective_date = effectiveDate.toISOString();
    }
    return this.http.patch<{ ok: boolean; status: number; data: any }>(
      url,
      body
    );
  }

  /**
   * For each `site::code` key currently shown as a mismatch for this domain, returns
   * the ISO date its NetSapiens-side quantity actually changed to its current value —
   * derived from nightly hosted snapshots (SPD-3203). A key with no entry means the
   * change is older than the retained snapshot history; the caller should fall back to
   * its own default effective date.
   */
  public getChangeDatesForDomain(
    domain: string,
    customerId: number,
    keys: string[]
  ) {
    return this.http.post<Record<string, string>>(
      `${this.base}/overview/${domain}/change-dates`,
      { customerId, keys }
    );
  }

  public createServiceProduct(
    customerId: number,
    productCode: string,
    quantity: number,
    rate?: number,
    description?: string,
    effectiveDate?: Date
  ) {
    const url = `${this.base}/customers/${customerId}/service-products`;
    const body: {
      product_code: string;
      quantity: number;
      rate?: number;
      description?: string;
      effective_date?: string;
    } = {
      product_code: productCode,
      quantity,
    };
    // Send rate if it's defined and not null (including 0 as a valid value)
    if (rate !== undefined && rate !== null) {
      body.rate = rate;
    }
    if (description) {
      body.description = description;
    }
    if (effectiveDate) {
      body.effective_date = effectiveDate.toISOString();
    }

    return this.http.post<{ ok: boolean; status: number; data: any }>(
      url,
      body
    );
  }

  public updateStaticService(
    customerId: number,
    serviceProductId: number,
    quantity: number,
    effectiveDate?: Date
  ) {
    return this.revioUpdateQuantity(
      customerId,
      serviceProductId,
      quantity,
      effectiveDate
    );
  }

  public deleteStaticService(
    customerId: number,
    serviceProductId: number,
    effectiveDate?: Date
  ) {
    return this.revioDeleteServiceProduct(
      customerId,
      serviceProductId,
      effectiveDate
    );
  }

  public revioDeleteServiceProduct(
    customerId: number,
    serviceProductId: number,
    effectiveDate?: Date
  ) {
    let url = `${this.base}/customers/${customerId}/service-products/${serviceProductId}`;
    if (effectiveDate) {
      const params = new HttpParams().set(
        'effective_date',
        effectiveDate.toISOString()
      );
      url = `${url}?${params.toString()}`;
    }
    return this.http.delete<{ ok: boolean; status: number; data: any }>(url);
  }

  public getProducts(): Observable<Product[]> {
    const url = `${this.base}/products`;
    return this.http.get<Product[]>(url);
  }

  public revioCreateChildSite(
    parentId: number,
    createCustomerDto: {
      bill_profile_id: number;
      billing_address: {
        company_name?: string;
        first_name?: string;
        last_name?: string;
        line_1: string;
        line_2?: string;
        city: string;
        state_or_province: string;
        postal_code: string;
        country_code?: string;
      };
      service_address?: {
        company_name?: string;
        first_name?: string;
        last_name?: string;
        line_1: string;
        line_2?: string;
        city: string;
        state_or_province: string;
        postal_code: string;
        country_code?: string;
      };
      listing_address?: {
        company_name?: string;
        first_name?: string;
        last_name?: string;
        line_1: string;
        line_2?: string;
        city: string;
        state_or_province: string;
        postal_code: string;
        country_code?: string;
      };
      class?: string;
      account_number?: string;
    }
  ) {
    const url = `${this.base}/customers/${parentId}/children`;
    return this.http.post<{ ok: boolean; status: number; data: any }>(
      url,
      createCustomerDto
    );
  }

  public revioDeleteCustomer(customerId: number) {
    const url = `${this.base}/customers/${customerId}`;
    return this.http.delete<{ ok: boolean; status: number; data: any }>(url);
  }

  public searchProductsByCode(code: string): Observable<Product[]> {
    const url = `${this.base}/products/search`;
    const params = new HttpParams().set('code', code);
    return this.http.get<Product[]>(url, { params });
  }

  // Providers endpoints
  public getProviders(): Observable<
    Array<{ id: number; description: string; code: string }>
  > {
    const url = `${this.base}/providers`;
    return this.http.get<
      Array<{ id: number; description: string; code: string }>
    >(url);
  }

  public searchProvidersByDescription(
    description: string
  ): Observable<Array<{ id: number; description: string; code: string }>> {
    const url = `${this.base}/providers/search`;
    const params = new HttpParams().set('description', description);
    return this.http.get<
      Array<{ id: number; description: string; code: string }>
    >(url, { params });
  }

  public getProviderById(
    id: number
  ): Observable<{ id: number; description: string; code: string }> {
    const url = `${this.base}/providers/${id}`;
    return this.http.get<{ id: number; description: string; code: string }>(
      url
    );
  }

  // Service Types endpoints
  public getServiceTypes(): Observable<
    Array<{ id: number; description: string; code: string }>
  > {
    const url = `${this.base}/service-types`;
    return this.http.get<
      Array<{ id: number; description: string; code: string }>
    >(url);
  }

  public searchServiceTypesByDescription(
    description: string
  ): Observable<Array<{ id: number; description: string; code: string }>> {
    const url = `${this.base}/service-types/search`;
    const params = new HttpParams().set('description', description);
    return this.http.get<
      Array<{ id: number; description: string; code: string }>
    >(url, { params });
  }

  public getServiceTypeById(
    id: number
  ): Observable<{ id: number; description: string; code: string }> {
    const url = `${this.base}/service-types/${id}`;
    return this.http.get<{ id: number; description: string; code: string }>(
      url
    );
  }

  // Product Types endpoints
  public getProductTypes(): Observable<Array<{ id: number; code: string }>> {
    const url = `${this.base}/product-types`;
    return this.http.get<Array<{ id: number; code: string }>>(url);
  }

  public searchProductTypesByCode(
    code: string
  ): Observable<Array<{ id: number; code: string }>> {
    const url = `${this.base}/product-types/search`;
    const params = new HttpParams().set('code', code);
    return this.http.get<Array<{ id: number; code: string }>>(url, { params });
  }

  public getProductTypeById(
    id: number
  ): Observable<{ id: number; code: string }> {
    const url = `${this.base}/product-types/${id}`;
    return this.http.get<{ id: number; code: string }>(url);
  }

  // Services endpoints
  public getServices(): Observable<
    Array<{
      id: number;
      customerId: number | null;
      customerName: string | null;
      serviceTypeId: number | null;
      serviceTypeDescription: string | null;
      serviceTypeCode: string | null;
      providerId: number | null;
      providerDescription: string | null;
      providerCode: string | null;
      status: string | null;
    }>
  > {
    const url = `${this.base}/services`;
    return this.http.get<
      Array<{
        id: number;
        customerId: number | null;
        customerName: string | null;
        serviceTypeId: number | null;
        serviceTypeDescription: string | null;
        serviceTypeCode: string | null;
        providerId: number | null;
        providerDescription: string | null;
        providerCode: string | null;
        status: string | null;
      }>
    >(url);
  }

  public searchServicesByCustomerId(customerId: number): Observable<
    Array<{
      id: number;
      customerId: number | null;
      customerName: string | null;
      serviceTypeId: number | null;
      serviceTypeDescription: string | null;
      serviceTypeCode: string | null;
      providerId: number | null;
      providerDescription: string | null;
      providerCode: string | null;
      status: string | null;
    }>
  > {
    const url = `${this.base}/services/search`;
    const params = new HttpParams().set('customerId', customerId.toString());
    return this.http.get<
      Array<{
        id: number;
        customerId: number | null;
        customerName: string | null;
        serviceTypeId: number | null;
        serviceTypeDescription: string | null;
        serviceTypeCode: string | null;
        providerId: number | null;
        providerDescription: string | null;
        providerCode: string | null;
        status: string | null;
      }>
    >(url, { params });
  }

  public getServiceById(id: number): Observable<{
    id: number;
    customerId: number;
    serviceTypeId: number;
    providerId: number;
    status: string | null;
  }> {
    const url = `${this.base}/services/${id}`;
    return this.http.get<{
      id: number;
      customerId: number;
      serviceTypeId: number;
      providerId: number;
      status: string | null;
    }>(url);
  }

  public getBillingDomainConfigs(): Observable<DomainBillingConfig[]> {
    return this.http.get<DomainBillingConfig[]>(`${this.base}/overview/domains`);
  }

  public updateDomainEnableForBilling(domain: string, enableForBilling: boolean): Observable<void> {
    return this.http.patch<void>(
      `${this.base}/overview/domains/${encodeURIComponent(domain)}`,
      { enableForBilling }
    );
  }

  public bulkUpdateEnableForBilling(updates: { domain: string; enableForBilling: boolean }[]): Observable<void> {
    return this.http.patch<void>(`${this.base}/overview/domains/bulk`, { updates });
  }

  public computeBillingOverviewStats(
    entries: { domain: string; customerId: number }[]
  ): Observable<DomainOverviewRow[]> {
    return this.http.post<DomainOverviewRow[]>(`${this.base}/overview/batch-stats`, { entries });
  }

  // Non-billable entries endpoints
  public getNonBillableEntries(
    domain?: string | null
  ): Observable<NonBillableEntryDto[]> {
    const url = `${this.base}/non-billable`;
    const params = domain ? new HttpParams().set('domain', domain) : new HttpParams();
    return this.http.get<NonBillableEntryDto[]>(url, { params });
  }

  public createNonBillableEntry(
    body: CreateNonBillableEntryRequest
  ): Observable<NonBillableEntryDto> {
    const url = `${this.base}/non-billable`;
    return this.http.post<NonBillableEntryDto>(url, body);
  }

  public deleteNonBillableEntry(id: number): Observable<void> {
    const url = `${this.base}/non-billable/${id}`;
    return this.http.delete<void>(url);
  }

  public getStaleNonBillableEntries(domain: string): Observable<NonBillableEntryDto[]> {
    const url = `${this.base}/non-billable/stale`;
    const params = new HttpParams().set('domain', domain);
    return this.http.get<NonBillableEntryDto[]>(url, { params });
  }

  public cleanupStaleNonBillableEntries(domain: string): Observable<NonBillableEntryDto[]> {
    const url = `${this.base}/non-billable/cleanup`;
    const params = new HttpParams().set('domain', domain);
    return this.http.delete<NonBillableEntryDto[]>(url, { params });
  }

  public getAllStaleNonBillableEntries(): Observable<NonBillableEntryDto[]> {
    const url = `${this.base}/non-billable/stale-all`;
    return this.http.get<NonBillableEntryDto[]>(url);
  }

  public cleanupAllStaleNonBillableEntries(): Observable<NonBillableEntryDto[]> {
    const url = `${this.base}/non-billable/cleanup-all`;
    return this.http.delete<NonBillableEntryDto[]>(url);
  }

  public exportNonBillableEntries(
    domain?: string | null
  ): Observable<string> {
    const url = `${this.base}/non-billable/export`;
    const params = domain ? new HttpParams().set('domain', domain) : new HttpParams();
    const options = { params, responseType: 'text' as 'json' };
    return this.http.get<string>(url, options);
  }

  public importNonBillableEntries(
    entries: ImportNonBillableEntryRequest[]
  ): Observable<ImportNonBillableResult> {
    const url = `${this.base}/non-billable/import`;
    return this.http.post<ImportNonBillableResult>(url, { entries });
  }

  // Device models endpoints
  public getDeviceModels(): Observable<DeviceModelDto[]> {
    return this.http.get<DeviceModelDto[]>(`${this.base}/device-models`);
  }

  public createDeviceModel(
    dto: CreateDeviceModelRequest
  ): Observable<DeviceModelDto> {
    return this.http.post<DeviceModelDto>(`${this.base}/device-models`, dto);
  }

  public updateDeviceModel(
    id: number,
    dto: UpdateDeviceModelRequest
  ): Observable<DeviceModelDto> {
    return this.http.put<DeviceModelDto>(
      `${this.base}/device-models/${id}`,
      dto
    );
  }

  public deleteDeviceModel(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/device-models/${id}`);
  }

  public getNsCatalog(): Observable<NsCatalogEntry[]> {
    return this.http.get<NsCatalogEntry[]>(`${this.base}/device-models/ns-catalog`);
  }

  public discoverDeviceModels(): Observable<DiscoveredModel[]> {
    return this.http.get<DiscoveredModel[]>(`${this.base}/device-models/discover`);
  }

  public bulkCreateDeviceModels(
    dto: BulkCreateDeviceModelRequest
  ): Observable<DeviceModelDto[]> {
    return this.http.post<DeviceModelDto[]>(`${this.base}/device-models/bulk`, dto);
  }

  public resetDeviceModelsToDefaults(): Observable<DeviceModelDto[]> {
    return this.http.post<DeviceModelDto[]>(`${this.base}/device-models/reset-defaults`, {});
  }

  public getDeviceModelDomains(): Observable<DomainItem[]> {
    return this.http.get<DomainItem[]>(`${this.base}/device-models/domains`);
  }

  /**
   * Scans one domain for device-model occurrences. Set `includeUserAgents` when validating a
   * USER_AGENT_CONTAINS entry: the provisioned inventory carries no registration user-agent, so
   * the backend has to sweep the domain's registered devices, which is markedly slower.
   */
  public scanDomain(domain: string, includeUserAgents = false): Observable<ScanDomainResult> {
    return this.http.get<ScanDomainResult>(`${this.base}/device-models/scan-domain`, {
      params: includeUserAgents ? { domain, includeUserAgents: 'true' } : { domain },
    });
  }
}
